import React, { useCallback, useState } from 'react';
import { ResourceOptionType, TeamMemberSchema } from '../data/types';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';
import DesktopUserAvatar from './Desktop/UserAvatar';
import MobileUserAvatar from './Mobile/UserAvatar';

export const getTeamMemberDisplayName = (member: TeamMemberSchema) =>
  [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || member.email;

export const getResourceCapacityLimits = (
  resourceOptions?: ResourceOptionType
): { totalCapacity?: number; teamMemberMaxCapacity?: number } => {
  const totalCapacity = resourceOptions?.floor_plan?.max_capacity;
  if (typeof totalCapacity !== 'number' || totalCapacity <= 0) {
    return {};
  }

  return {
    totalCapacity,
    teamMemberMaxCapacity: Math.max(0, totalCapacity - 1),
  };
};

export const getResourceTeamMemberMaxCapacity = (
  resourceOptions?: ResourceOptionType
): number | undefined => getResourceCapacityLimits(resourceOptions).teamMemberMaxCapacity;

const formatCapacityHint = ({
  teamLimit,
  totalCapacity,
  isAtMaxCapacity,
}: {
  teamLimit: number;
  totalCapacity: number;
  isAtMaxCapacity: boolean;
}) => {
  const totalLabel = `${totalCapacity} ${totalCapacity === 1 ? 'person' : 'people'} total including you`;

  if (teamLimit === 0) {
    return `No additional team members (${totalLabel})`;
  }

  if (isAtMaxCapacity) {
    return `Maximum of ${teamLimit} team member${teamLimit !== 1 ? 's' : ''} reached (${totalLabel})`;
  }

  return `Select up to ${teamLimit} team member${teamLimit !== 1 ? 's' : ''} (${totalLabel})`;
};

export type AddTeamMembersViewProps = {
  availableTeamMembers: TeamMemberSchema[];
  selectedTeamMemberIds: string[];
  setSelectedTeamMemberIds: React.Dispatch<React.SetStateAction<string[]>>;
  onInviteMember: (member: Omit<TeamMemberSchema, 'id'>) => void;
  isMobile?: boolean;
  /** Uses BookingDetailsForm label styling and full width when true. */
  embedded?: boolean;
  /** Max selectable team members (floor plan capacity minus the booking member). */
  maxCapacity?: number;
  /** Total floor plan capacity including the member making the booking. */
  totalCapacity?: number;
};

const AddTeamMembersView: React.FC<AddTeamMembersViewProps> = ({
  availableTeamMembers,
  selectedTeamMemberIds,
  setSelectedTeamMemberIds,
  onInviteMember,
  isMobile = false,
  embedded = false,
  maxCapacity,
  totalCapacity,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const UserAvatar = isMobile ? MobileUserAvatar : DesktopUserAvatar;

  const selectedCount = selectedTeamMemberIds.length;
  const resolvedTotalCapacity =
    totalCapacity ?? (maxCapacity != null ? maxCapacity + 1 : undefined);
  const resolvedTeamLimit =
    maxCapacity ?? (resolvedTotalCapacity != null ? Math.max(0, resolvedTotalCapacity - 1) : undefined);
  const hasCapacityLimit = resolvedTotalCapacity != null && resolvedTeamLimit != null;
  const isAtMaxCapacity = hasCapacityLimit && selectedCount >= resolvedTeamLimit;
  const summaryText =
    selectedCount === 0
      ? 'Select or add team members'
      : `${selectedCount} team member${selectedCount !== 1 ? 's' : ''} added`;

  const toggleMember = (memberId: string) => {
    setSelectedTeamMemberIds((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      }
      if (resolvedTeamLimit != null && prev.length >= resolvedTeamLimit) {
        return prev;
      }
      return [...prev, memberId];
    });
  };

  const handleInviteMember = useCallback(
    (member: Omit<TeamMemberSchema, 'id'>) => {
      if (resolvedTeamLimit != null && selectedTeamMemberIds.length >= resolvedTeamLimit) {
        return;
      }
      onInviteMember(member);
    },
    [resolvedTeamLimit, onInviteMember, selectedTeamMemberIds.length]
  );

  const existingMemberEmails = availableTeamMembers.map((member) =>
    member.email.trim().toLowerCase()
  );

  const title = embedded ? 'Team Members' : 'Add Team Members';
  const touchClass = isMobile ? ' touch-manipulation' : '';
  const avatarSize = isMobile && !embedded ? 'sm' : 'md';
  const containerClass = embedded ? 'w-full' : isMobile ? 'w-full mx-auto' : 'w-11/12 mx-auto';

  return (
    <>
      <div className={`flex flex-col text-left ${containerClass}`}>
        {embedded ? (
          <label className="block text-[#808080] text-[16px] font-raleway font-medium">{title}</label>
        ) : (
          <p className="font-raleway text-[16px] text-black">{title}</p>
        )}

        {hasCapacityLimit && (
          <p
            className={`font-open-sans text-[13px] mt-1 ${isAtMaxCapacity ? 'text-[#B45309]' : 'text-[#808080]'}`}
            data-testid="team-members-capacity-hint"
          >
            {formatCapacityHint({
              teamLimit: resolvedTeamLimit!,
              totalCapacity: resolvedTotalCapacity!,
              isAtMaxCapacity,
            })}
          </p>
        )}

        <div className={embedded ? 'mt-1' : 'mt-3'}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className={`w-full border-[1px] border-[#E9E9E9] rounded-lg bg-white px-4 py-3 flex items-center justify-between text-left${touchClass}`}
          >
            <span className={`font-open-sans text-[16px] ${selectedCount === 0 ? 'text-[#808080]' : 'text-[#333333]'}`}>
              {summaryText}
            </span>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDropdownOpen && (
            <div
              className="mt-1 border-[1px] border-[#E9E9E9] rounded-lg bg-white shadow-sm"
              data-testid="team-members-dropdown-panel"
            >
              <div className="max-h-[min(20rem,calc(100vh-12rem))] overflow-y-auto overscroll-contain">
                {availableTeamMembers.map((member) => {
                  const isSelected = selectedTeamMemberIds.includes(member.id);
                  const isSelectionDisabled = !isSelected && isAtMaxCapacity;
                  return (
                    <label
                      key={member.id}
                      className={`flex items-center gap-3 px-4 py-3 border-0${
                        isSelected ? ' bg-[#F8F8F8]' : ' bg-white'
                      }${
                        isSelectionDisabled
                          ? ' opacity-50 cursor-not-allowed'
                          : ' cursor-pointer hover:bg-[#FAFAFA]'
                      }${touchClass}`}
                    >
                      <UserAvatar firstName={member.first_name} lastName={member.last_name} email={member.email} size={avatarSize} />
                      <span className="flex-1 font-open-sans text-[15px] text-[#333333]">
                        {getTeamMemberDisplayName(member)}
                      </span>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isSelectionDisabled}
                        onChange={() => toggleMember(member.id)}
                        className="sr-only"
                      />
                      <div
                        className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? 'border-primaryDark-300 bg-primaryDark-300'
                            : 'border-[#D9D8D8] bg-white'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </label>
                  );
                })}
                <button
                  type="button"
                  data-testid="add-team-member-button"
                  disabled={isAtMaxCapacity}
                  onClick={() => {
                    if (isAtMaxCapacity) {
                      return;
                    }
                    setIsDropdownOpen(false);
                    setIsInviteModalOpen(true);
                  }}
                  className={`w-full border-0 bg-white px-4 py-3 flex items-center text-primaryDark-400 font-open-sans text-[14px] font-semibold${
                    isAtMaxCapacity ? ' opacity-50 cursor-not-allowed' : ' hover:bg-[#FAFAFA]'
                  }${touchClass}`}
                >
                  <span className="mr-2 text-[18px] leading-none">+</span>
                  Add team member
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <InviteTeamMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteMember}
        existingMemberEmails={existingMemberEmails}
        isMobile={isMobile}
      />
    </>
  );
};

export default AddTeamMembersView;
